// Chakra v3 has completely changed the Radio API to use compound components
// We'll provide a compatibility layer for the old useRadio hook
import { useCallback } from "react";

export type UseRadioProps = {
  value?: string | number;
  isChecked?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
  name?: string;
  defaultChecked?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  id?: string;
} & Record<string, unknown>;

export const useRadio = (props: UseRadioProps = {}) => {
  const {
    value,
    isChecked,
    isDisabled,
    isReadOnly,
    isInvalid,
    isRequired,
    name,
    defaultChecked,
    onChange,
    id,
    ...rest
  } = props;

  const getInputProps = useCallback(
    <T extends Record<string, unknown>>(additionalProps: T = {} as T) => ({
      ...additionalProps,
      type: "radio" as const,
      value,
      checked: isChecked,
      disabled: isDisabled || isReadOnly,
      required: isRequired,
      name,
      defaultChecked,
      onChange,
      id,
      "aria-invalid": isInvalid,
      ...rest,
    }),
    [
      value,
      isChecked,
      isDisabled,
      isReadOnly,
      isRequired,
      name,
      defaultChecked,
      onChange,
      id,
      isInvalid,
      rest,
    ]
  );

  const getCheckboxProps = useCallback(
    <T extends Record<string, unknown>>(additionalProps: T = {} as T) => ({
      ...additionalProps,
      "data-checked": isChecked ? "" : undefined,
      "data-disabled": isDisabled ? "" : undefined,
      "data-invalid": isInvalid ? "" : undefined,
      "data-readonly": isReadOnly ? "" : undefined,
    }),
    [isChecked, isDisabled, isInvalid, isReadOnly]
  );

  const getLabelProps = useCallback(
    <T extends Record<string, unknown>>(additionalProps: T = {} as T) => ({
      ...additionalProps,
      htmlFor: id,
      "data-disabled": isDisabled ? "" : undefined,
    }),
    [id, isDisabled]
  );

  const getRootProps = useCallback(
    <T extends Record<string, unknown>>(additionalProps: T = {} as T) => ({
      ...additionalProps,
      "data-checked": isChecked ? "" : undefined,
      "data-disabled": isDisabled ? "" : undefined,
    }),
    [isChecked, isDisabled]
  );

  return {
    state: {
      isChecked: !!isChecked,
      isDisabled: !!isDisabled,
      isReadOnly: !!isReadOnly,
      isInvalid: !!isInvalid,
      isRequired: !!isRequired,
    },
    getInputProps,
    getCheckboxProps,
    getLabelProps,
    getRootProps,
    htmlProps: rest,
  };
};

export default useRadio;
